import { useEffect, useRef } from 'react';

import { isInIframe } from '../utils';

const useBeforeunload = (handler: (env?: BeforeUnloadEvent) => void): void => {
  const eventListenerRef = useRef(null);

  useEffect(() => {
    eventListenerRef.current = (event: BeforeUnloadEvent) => {
      const returnValue = handler?.(event);

      // Handle legacy `event.returnValue` property
      // https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event
      if (typeof returnValue === 'string') {
        event.returnValue = returnValue;
        return event;
      }
      // Chrome doesn't support `event.preventDefault()` on `BeforeUnloadEvent`,
      // instead it requires `event.returnValue` to be set
      // https://developer.mozilla.org/en-US/docs/Web/API/WindowEventHandlers/onbeforeunload#browser_compatibility
      if (event.defaultPrevented) {
        event.returnValue = '';
        return event;
      }
    };
  }, [handler]);

  useEffect(() => {
    const isIframe = isInIframe();

    const eventListener = (event: Event) => eventListenerRef.current(event);
    window.addEventListener('beforeunload', eventListener);
    if (isIframe) window.addEventListener('pagehide', eventListener);

    return () => {
      window.removeEventListener('beforeunload', eventListener);
      if (isIframe) window.addEventListener('pagehide', eventListener);
    };
  }, []);
};

export default useBeforeunload;
